import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Field from "@/components/admin/Field";
import AiDraftPanel from "@/components/admin/AiDraftPanel";
import { recordContext } from "@/lib/aiStudio";

export default function CollectionsEditor({ collection, title, fields, newItem, display }) {
  const [items, setItems] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = () => api.get(`/admin/${collection}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => {
    load();
  }, []);

  const saveItem = async (item) => {
    try {
      await api.put(`/admin/${collection}/${item.id}`, item);
      toast.success("Saved — live on the website");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addItem = async () => {
    try {
      const res = await api.post(`/admin/${collection}`, newItem);
      toast.success("Item added");
      setItems((prev) => [...(prev || []), res.data]);
      setOpenId(res.data.id);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Delete this item? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/${collection}/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (items === null) return <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>;

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h3 className="font-head text-2xl">{title}</h3>
        <button onClick={addItem} data-testid={`${collection}-add-btn`} className="btn-gold h-10 px-5 text-xs">
          + Add new
        </button>
      </div>
      <div className="space-y-3">
        {items.map((item, i) => {
          const itemId = item.id || String(i);
          const isOpen = openId === itemId;
          return (
            <div key={itemId} className="border border-border" style={{ borderRadius: "var(--radius)" }}>
              <button
                type="button"
                data-testid={`${collection}-item-${i}-toggle`}
                onClick={() => setOpenId(isOpen ? null : itemId)}
                className="flex w-full items-center justify-between px-5 py-4 text-start"
              >
                <span className="font-medium">{display ? display(item) : item.name}</span>
                <span className="text-xs text-muted-foreground">{isOpen ? "Collapse" : "Edit"}</span>
              </button>
              {isOpen && (
                <div className="grid gap-4 border-t border-border px-5 pb-5 pt-5 md:grid-cols-2">
                  <AiDraftPanel
                    kind={collection}
                    fields={fields}
                    item={item}
                    testKey={`${collection}-${i}`}
                    onApply={(patch) => setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)))}
                  />
                  {fields.map((f) => (
                    <Field
                      key={f.key}
                      field={f}
                      value={item[f.key]}
                      record={item}
                      context={recordContext(item, fields, f.key)}
                      onChange={(v) => setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, [f.key]: v } : it)))}
                    />
                  ))}
                  <div className="flex justify-end gap-3 pt-2 md:col-span-2">
                    <button
                      onClick={() => deleteItem(item.id)}
                      data-testid={`${collection}-item-${i}-delete`}
                      className="btn-outline h-10 border-destructive/40 text-xs text-destructive"
                    >
                      Delete
                    </button>
                    <button onClick={() => saveItem(item)} data-testid={`${collection}-item-${i}-save`} className="btn-gold h-10 text-xs">
                      Save
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {items.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Nothing here yet — add the first item.</p>}
      </div>
    </div>
  );
}
